import { assignPersonality } from './ai/personality.js';
import { createPrng, type Prng } from './rng.js';
import {
  type GameState,
  type MapDefinition,
  type Player,
  type PlayerId,
  type RegionId,
  type TerritoryId,
  type TerritoryState,
  type TurnContext,
} from './types.js';

export const STARTING_UNITS_PER_TERRITORY = 3;

/**
 * Input shape for createInitialState. Both `eliminated` (always false
 * at start) and `team` (defaulted to seat-index FFA when omitted) are
 * optional on input — createInitialState fills them in. Lobby callers
 * that want explicit team assignments (2v2 etc.) provide `team`;
 * legacy callers and tests can omit it.
 */
export type SeatInput = Omit<Player, 'eliminated' | 'team'> & {
  readonly team?: number;
};

export interface CreateGameOptions {
  readonly map: MapDefinition;
  readonly players: readonly SeatInput[];
  readonly seed: number;
}

export function createInitialState(opts: CreateGameOptions): GameState {
  const { map, players: rawPlayers, seed } = opts;

  if (rawPlayers.length < 2) {
    throw new Error('createInitialState requires at least 2 players');
  }
  if (rawPlayers.length > map.territories.length) {
    throw new Error('createInitialState: more players than territories');
  }
  if (new Set(rawPlayers.map((p) => p.id)).size !== rawPlayers.length) {
    throw new Error('createInitialState: duplicate player ids');
  }

  const rng = createPrng(seed);

  // Assign a personality to each AI seat in input order. Seat 0 of AIs
  // gets Aggressive, seat 1 Cautious, seat 2 Wildcard, then wraps. If
  // the caller provided a personality already (lobby flow), it wins.
  // Humans get nothing — the field is omitted via
  // exactOptionalPropertyTypes rather than set to undefined.
  //
  // Team assignment: default each seat to a unique team number
  // (FFA — every seat on its own team). Caller can override via the
  // `team` field on rawPlayer to set up team play (2v2, 1v3, etc.).
  let aiSeatIndex = 0;
  const players: readonly Player[] = rawPlayers.map((p, idx) => {
    const team = p.team ?? idx + 1;
    if (p.kind === 'ai') {
      const personality = p.personality ?? assignPersonality(aiSeatIndex);
      aiSeatIndex++;
      return { ...p, eliminated: false, team, personality };
    }
    return { ...p, eliminated: false, team };
  });

  // Team-interleaved seat order. Teammates never go back-to-back when
  // it can be avoided — the round-robin pulls one seat from each team's
  // bucket per pass. Within a team and across teams the order is still
  // random (seeded), so the opener varies between games.
  //
  // Examples (2v2 input T1,T1,T2,T2 → output is some permutation of
  // T1,T2,T1,T2). Uneven splits like 3v1 spread the minority team out:
  // T1,T2,T1,T1 or T1,T1,T2,T1, never T1,T1,T1,T2 (which would let the
  // larger team take three uninterrupted turns).
  //
  // Also decides which seat gets the extra territory when territory
  // count doesn't divide evenly.
  const playerOrder = interleaveByTeam(players, rng);

  // Random territory distribution, dealt round-robin into the shuffled order.
  const shuffledTerritories = shuffle(
    map.territories.map((t) => t.id),
    rng
  );

  const territories: Record<TerritoryId, TerritoryState> = {};
  for (let i = 0; i < shuffledTerritories.length; i++) {
    const tid = shuffledTerritories[i]!;
    const owner = playerOrder[i % playerOrder.length]!;
    territories[tid] = { owner, units: STARTING_UNITS_PER_TERRITORY };
  }

  const firstPlayer = playerOrder[0]!;

  // First player's reinforcement count is computed against the freshly
  // dealt board; no one controls a full region at game start, so the
  // bonus is always 0 here, but the call site uses the unified helper
  // for consistency.
  const turn: TurnContext = {
    currentPlayer: firstPlayer,
    phase: 'deploy',
    attack: { kind: 'free' },
    reinforcementsRemaining: baseReinforcements(
      countOwned(territories, firstPlayer)
    ),
    fortifyUsed: false,
    deploysThisTurn: [],
  };

  return {
    mapId: map.id,
    // Turn 0 is the setup round: each seat does one deploy-only pass before
    // any attacks happen (mitigates first-mover advantage). When the seat
    // rotation wraps back to playerOrder[0], turnNumber bumps to 1 and the
    // normal deploy → attack → fortify cycle begins.
    turnNumber: 0,
    players,
    playerOrder,
    territories,
    turn,
    status: { kind: 'inProgress' },
    rngSeed: rng.getSeed(),
    eliminationOrder: [],
    history: [],
    // v0.9.9 Hourglass — no skips yet at game start.
    consecutiveAutoSkips: {},
  };
}

/**
 * Baseline reinforcement count from territory count alone — `max(3,
 * floor(owned / 3))`. Exposed separately from `computeReinforcements`
 * so unit tests can pin the floor formula without constructing a full
 * GameState. Most callers want `computeReinforcements`, which adds
 * region-bonus on top.
 */
export function baseReinforcements(ownedCount: number): number {
  return Math.max(3, Math.floor(ownedCount / 3));
}

/**
 * Total reinforcements granted at the start of a player's deploy phase:
 * the territory-count baseline plus the sum of bonuses for every region
 * the player fully controls.
 */
export function computeReinforcements(
  state: GameState,
  map: MapDefinition,
  player: PlayerId
): number {
  const owned = countOwned(state.territories, player);
  return baseReinforcements(owned) + computeRegionBonus(state, map, player);
}

/**
 * Reinforcement bonus a specific player earns from team-controlled
 * regions. In FFA (each player on their own team) this is the full
 * `territoryBonus` for every region the player solo-controls — the
 * pre-team behavior.
 *
 * In team play, a region is "controlled" when the player's team
 * jointly owns every tile, but the bonus is split among the
 * teammates proportionally to how many of the region's tiles each
 * one personally holds. The split rule:
 *
 *   1. Each teammate gets `floor(bonus × tiles_held / region_size)`.
 *   2. The remainder (always 0..bonus-1) goes to the player who owns
 *      strictly more than 50% of the region's tiles. This makes
 *      majority-investment pay off — the player who did most of the
 *      work to capture the region gets the rounding bump.
 *   3. If no teammate has >50% (e.g. an even 2/2 split, or a 3-way
 *      1/1/1), the leftover walks down by descending tile count
 *      with seat-order tiebreak, one unit at a time. Keeps things
 *      deterministic and roughly proportional.
 *
 * For the user-specified 6/4 split on +6: floors 3 + 2 = 5,
 * leftover 1, the 6-tile holder is >50% so they get the +1, ending
 * at 4 and 2. For 3/1 on +3: floors 2 + 0 = 2, leftover 1, the
 * 3-tile holder is >50% so they get the +1, ending at 3 and 0
 * (the minority gets nothing — investing matters).
 */
export function computeRegionBonus(
  state: GameState,
  map: MapDefinition,
  player: PlayerId
): number {
  let total = 0;
  for (const region of map.regions) {
    if (!controlsRegion(state, map, region.id, player)) continue;
    total += playerShareOfRegion(state, map, region, player);
  }
  return total;
}

/**
 * One player's proportional slice of a region's bonus, given that
 * the region is team-controlled. See `computeRegionBonus` for the
 * apportionment rule.
 */
function playerShareOfRegion(
  state: GameState,
  map: MapDefinition,
  region: { readonly id: RegionId; readonly territoryBonus: number },
  player: PlayerId
): number {
  const counts = new Map<PlayerId, number>();
  let regionSize = 0;
  for (const t of map.territories) {
    if (t.region !== region.id) continue;
    regionSize++;
    const ts = state.territories[t.id];
    if (!ts) continue;
    counts.set(ts.owner, (counts.get(ts.owner) ?? 0) + 1);
  }
  if (regionSize === 0) return 0;

  const B = region.territoryBonus;
  if (B === 0) return 0;

  // Floor share for each teammate.
  const floors = new Map<PlayerId, number>();
  for (const [pid, c] of counts) {
    floors.set(pid, Math.floor((B * c) / regionSize));
  }
  const floorSum = [...floors.values()].reduce((s, f) => s + f, 0);
  let leftover = B - floorSum;
  if (leftover === 0) return floors.get(player) ?? 0;

  // Find a strict majority owner (>50%). At most one player can
  // have this, so when present the entire remainder is theirs.
  const halfThreshold = regionSize / 2;
  let majorityOwner: PlayerId | null = null;
  for (const [pid, c] of counts) {
    if (c > halfThreshold) {
      majorityOwner = pid;
      break;
    }
  }
  if (majorityOwner !== null) {
    if (majorityOwner === player) {
      return (floors.get(player) ?? 0) + leftover;
    }
    return floors.get(player) ?? 0;
  }

  // No strict majority. Walk teammates in descending tile-count
  // order (tiebreak seat order ascending), distributing one leftover
  // unit per teammate. Deterministic and roughly proportional.
  const sorted = [...counts.entries()].sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return state.playerOrder.indexOf(a[0]) - state.playerOrder.indexOf(b[0]);
  });
  for (const [pid] of sorted) {
    if (leftover <= 0) break;
    if (pid === player) return (floors.get(player) ?? 0) + 1;
    leftover--;
  }
  return floors.get(player) ?? 0;
}

/**
 * True iff `player`'s TEAM controls every territory in `regionId`.
 * In FFA (each player on their own team) this reduces to "this
 * player owns every tile" — backward-compatible with the pre-team
 * semantics. In team play, two teammates jointly holding all tiles
 * counts as control: e.g. on Terra, You + Navy together holding all
 * 8 Asia tiles gives T2 region control.
 *
 * Empty regions (which shouldn't exist in valid maps) trivially
 * return true; that's intentional defensive behavior.
 */
export function controlsRegion(
  state: GameState,
  map: MapDefinition,
  regionId: RegionId,
  player: PlayerId
): boolean {
  const me = state.players.find((p) => p.id === player);
  if (!me) return false;
  const teamByOwner = new Map(state.players.map((p) => [p.id, p.team]));
  for (const t of map.territories) {
    if (t.region !== regionId) continue;
    const ts = state.territories[t.id];
    if (!ts) return false;
    if (teamByOwner.get(ts.owner) !== me.team) return false;
  }
  return true;
}

export function countOwned(
  territories: Readonly<Record<TerritoryId, TerritoryState>>,
  player: PlayerId
): number {
  let count = 0;
  for (const t of Object.values(territories)) {
    if (t.owner === player) count++;
  }
  return count;
}

function shuffle<T>(items: readonly T[], rng: Prng): readonly T[] {
  const arr = items.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = rng.nextInt(i + 1);
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  return arr;
}

/**
 * Build the turn rotation by round-robin pulling one seat from each
 * team's bucket per pass. Buckets and team order are shuffled first so
 * the result is random per seed — but teammates are spread out as far
 * as the team sizes allow.
 *
 * In FFA (every seat its own team) this reduces to a plain shuffle,
 * since every bucket has exactly one element.
 */
function interleaveByTeam(
  players: readonly Player[],
  rng: Prng
): readonly PlayerId[] {
  // Bucket seats by team, with each team's internal order shuffled.
  const byTeam = new Map<number, PlayerId[]>();
  for (const p of players) {
    const list = byTeam.get(p.team) ?? [];
    list.push(p.id);
    byTeam.set(p.team, list);
  }
  const teamKeys = shuffle([...byTeam.keys()], rng);
  const buckets = teamKeys.map(
    (k) => shuffle(byTeam.get(k)!, rng).slice() as PlayerId[]
  );

  // Round-robin drain. Each pass takes one seat from every non-empty
  // bucket in the (already shuffled) team order.
  const out: PlayerId[] = [];
  while (buckets.some((b) => b.length > 0)) {
    for (const bucket of buckets) {
      const next = bucket.shift();
      if (next !== undefined) out.push(next);
    }
  }
  return out;
}
